import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#selectGwfOpeningMedia();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #selectGwfOpeningMedia = () => {
    if (this.dataset.gwfVariantUpdate === 'true') return;

    const trySelect = (attempt = 0) => {
      if (!this.isConnected || this.dataset.gwfVariantUpdate === 'true') return;

      const marker = document.querySelector('[data-gwf-opening-media-id]');
      const openingMediaId = marker?.dataset.gwfOpeningMediaId;
      const slideshow = this.slideshow;
      const slides = slideshow?.refs?.slides;

      if (!openingMediaId || !slideshow || !slides?.length) {
        if (attempt < 12) window.setTimeout(() => trySelect(attempt + 1), 50);
        return;
      }

      const targetIndex = slides.findIndex((slide) =>
        Boolean(slide.querySelector(`[data-media-id="${CSS.escape(openingMediaId)}"]`))
      );

      if (targetIndex < 0) return;

      slideshow.select(targetIndex, undefined, { animate: false });

      if (slideshow.current !== targetIndex && attempt < 12) {
        window.setTimeout(() => trySelect(attempt + 1), 50);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => trySelect(), { once: true });
    } else {
      window.setTimeout(() => trySelect(), 0);
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    newMediaGallery.dataset.gwfVariantUpdate = 'true';
    const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    newSlideshow?.setAttribute('initial-slide', '0');

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom dialog component event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
